import { formatDateOnly } from "../public-site/publicSiteUtils";

const MEMBER_RESOURCE_NAME_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base"
});

export function publicReservationStatusLabel(status) {
  const labels = {
    confirmed: "Confirmed booking",
    standby: "Standby request",
    cancelled: "Cancelled",
    completed: "Completed",
    no_show: "No-show recorded"
  };
  return labels[status] || status || "Reservation";
}


export function toDateTimeLocalInput(date) {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

export function parseLocalDateTime(date, time = "00:00") {
  if (!date) {
    return null;
  }
  const parsed = new Date(`${date}T${time || "00:00"}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function roundDateToSlot(date, slotMinutes, direction = "up") {
  const next = new Date(date);
  next.setSeconds(0, 0);
  const slot = Math.max(Number.parseInt(slotMinutes || "30", 10) || 30, 15);
  const minute = next.getMinutes();
  const rounded = direction === "down"
    ? Math.floor(minute / slot) * slot
    : Math.ceil(minute / slot) * slot;
  next.setMinutes(rounded);
  if (rounded >= 60) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
  }
  return next;
}

export function localDateKey(date = new Date(), timezone = "America/Los_Angeles") {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).format(date);
  } catch {
    return toDateTimeLocalInput(date).slice(0, 10);
  }
}

export function localDayKey(date = new Date(), timezone = "America/Los_Angeles") {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "long"
    }).format(date).toLowerCase();
  } catch {
    const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return dayKeys[date.getDay()];
  }
}

export function businessHoursNextWindow(businessHours = null, reference = new Date()) {
  if (!businessHours?.weeklyHours) {
    return null;
  }
  const timezone = businessHours.timezone || "America/Los_Angeles";
  for (let offset = 0; offset <= 30; offset += 1) {
    const candidate = new Date(reference);
    candidate.setDate(candidate.getDate() + offset);
    candidate.setHours(12, 0, 0, 0);
    const dateKey = localDateKey(candidate, timezone);
    const exception = (businessHours.exceptions || []).find((item) => item?.date === dateKey);
    const schedule = exception || businessHours.weeklyHours[localDayKey(candidate, timezone)];
    if (!schedule || schedule.closed) {
      continue;
    }
    const periods = (schedule.periods || [])
      .filter((period) => period?.opens && period?.closes)
      .sort((left, right) => String(left.opens).localeCompare(String(right.opens)));
    for (const period of periods) {
      const periodStart = parseLocalDateTime(dateKey, period.opens);
      const periodEnd = parseLocalDateTime(dateKey, period.closes);
      if (!periodStart || !periodEnd || periodEnd <= reference) {
        continue;
      }
      const startsAt = periodStart < reference ? new Date(reference) : periodStart;
      return {
        startsAt,
        endsAt: periodEnd,
        label: `${period.opens} - ${period.closes}`
      };
    }
  }
  return null;
}

export function resourceExplicitlyAllowsAfterHours(resource = {}) {
  const policy = resource.availabilityPolicy || {};
  const rules = resource.reservationRules || {};
  return Boolean(
    policy.afterHoursEnabled ||
    policy.allowAfterHours ||
    policy.selfServiceAfterHours ||
    rules.afterHoursEnabled ||
    rules.allowAfterHours ||
    rules.selfServiceAfterHours
  );
}

export function resourceAvailabilityBounds(resource = {}, businessHours = null, reference = new Date()) {
  const policy = resource.availabilityPolicy || {};
  const shouldUseBusinessHours = policy.inheritRangeHours !== false ||
    (!resourceExplicitlyAllowsAfterHours(resource) && policy.openTime === "00:00" && policy.closeTime === "23:59");
  const businessWindow = shouldUseBusinessHours
    ? businessHoursNextWindow(businessHours, reference)
    : null;
  const policyStartsAt = parseLocalDateTime(policy.startsOn, policy.openTime || "00:00");
  const policyEndsAt = parseLocalDateTime(policy.endsOn, policy.closeTime || "23:59");
  const startsAt = [policyStartsAt, businessWindow?.startsAt]
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime())[0] || null;
  const endsAt = [policyEndsAt, businessWindow?.endsAt]
    .filter(Boolean)
    .sort((left, right) => left.getTime() - right.getTime())[0] || null;
  return {
    startsAt,
    endsAt,
    businessWindow
  };
}

export function resourceHasReservableWindow(resource = {}, reference = new Date(), businessHours = null) {
  const rules = resource.reservationRules || {};
  const slotMinutes = Math.max(Number.parseInt(rules.slotSizeMinutes || "30", 10) || 30, 15);
  const minDuration = Math.max(Number.parseInt(rules.minDurationMinutes || slotMinutes, 10) || slotMinutes, slotMinutes);
  const bounds = resourceAvailabilityBounds(resource, businessHours, reference);
  const earliestStart = roundDateToSlot(bounds.startsAt && bounds.startsAt > reference ? bounds.startsAt : reference, slotMinutes, "up");
  const earliestEnd = new Date(earliestStart);
  earliestEnd.setMinutes(earliestEnd.getMinutes() + minDuration);
  return !bounds.endsAt || earliestEnd <= bounds.endsAt;
}

export function defaultMemberReservationWindow(resource = {}, businessHours = null) {
  const rules = resource?.reservationRules || {};
  const slotMinutes = Math.max(Number.parseInt(rules.slotSizeMinutes || "30", 10) || 30, 15);
  const minDuration = Math.max(Number.parseInt(rules.minDurationMinutes || slotMinutes, 10) || slotMinutes, slotMinutes);
  const now = new Date();
  const bounds = resourceAvailabilityBounds(resource, businessHours, now);
  const startsAt = roundDateToSlot(new Date(), slotMinutes, "up");
  startsAt.setMinutes(startsAt.getMinutes() + slotMinutes);
  if (bounds.startsAt && startsAt < bounds.startsAt) {
    const boundedStart = roundDateToSlot(bounds.startsAt, slotMinutes, "up");
    startsAt.setTime(boundedStart.getTime());
  }
  const endsAt = new Date(startsAt);
  endsAt.setMinutes(endsAt.getMinutes() + minDuration);
  if (bounds.endsAt && endsAt > bounds.endsAt) {
    const latestStart = new Date(bounds.endsAt);
    latestStart.setMinutes(latestStart.getMinutes() - minDuration);
    const boundedStart = roundDateToSlot(latestStart, slotMinutes, "down");
    if (!bounds.startsAt || boundedStart >= bounds.startsAt) {
      startsAt.setTime(boundedStart.getTime());
      endsAt.setTime(startsAt.getTime());
      endsAt.setMinutes(endsAt.getMinutes() + minDuration);
    }
  }
  return {
    startsAt: toDateTimeLocalInput(startsAt),
    endsAt: toDateTimeLocalInput(endsAt)
  };
}

export function addMinutes(date, minutes) {
  const next = new Date(date);
  next.setMinutes(next.getMinutes() + minutes);
  return next;
}

export function minDate(...dates) {
  const validDates = dates.filter((date) => date instanceof Date && Number.isFinite(date.getTime()));
  if (validDates.length === 0) {
    return null;
  }
  return validDates.reduce((earliest, date) => (date < earliest ? date : earliest), validDates[0]);
}

export function memberReservationInputConstraints(resource = {}, businessHours = null, startsAtValue = "") {
  const rules = resource?.reservationRules || {};
  const slotMinutes = Math.max(Number.parseInt(rules.slotSizeMinutes || "30", 10) || 30, 15);
  const minDuration = Math.max(Number.parseInt(rules.minDurationMinutes || slotMinutes, 10) || slotMinutes, slotMinutes);
  const maxDuration = Math.max(Number.parseInt(rules.maxDurationMinutes || "0", 10) || 0, minDuration);
  const startDate = startsAtValue ? new Date(startsAtValue) : null;
  const validStart = startDate && Number.isFinite(startDate.getTime()) ? startDate : null;
  const bounds = resourceAvailabilityBounds(resource, businessHours, validStart || new Date());
  const endMin = validStart ? addMinutes(validStart, minDuration) : null;
  const endMax = validStart
    ? minDate(maxDuration ? addMinutes(validStart, maxDuration) : null, bounds.endsAt)
    : bounds.endsAt;
  const latestStart = bounds.endsAt ? addMinutes(bounds.endsAt, -minDuration) : null;

  return {
    endMax: endMax ? toDateTimeLocalInput(endMax) : undefined,
    endMin: endMin ? toDateTimeLocalInput(endMin) : undefined,
    startMax: latestStart ? toDateTimeLocalInput(latestStart) : undefined,
    startMin: bounds.startsAt ? toDateTimeLocalInput(bounds.startsAt) : undefined,
    stepSeconds: slotMinutes * 60
  };
}

export function memberReservationRuleSummary(resource = {}) {
  const rules = resource?.reservationRules || {};
  const min = Number.parseInt(rules.minDurationMinutes || "0", 10) || 0;
  const max = Number.parseInt(rules.maxDurationMinutes || "0", 10) || 0;
  const slot = Number.parseInt(rules.slotSizeMinutes || "0", 10) || 0;
  return [
    min ? `${min} min minimum` : "",
    max ? `${max} min maximum` : "",
    slot ? `${slot} min slots` : ""
  ].filter(Boolean).join(" / ") || "Range-managed reservation window";
}

export function businessHoursLocalParts(date = new Date(), timezone = "America/Los_Angeles") {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        weekday: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).formatToParts(date).map((part) => [part.type, part.value])
    );
    return {
      dayKey: String(parts.weekday || "").toLowerCase(),
      minutes: (Number.parseInt(parts.hour, 10) || 0) * 60 + (Number.parseInt(parts.minute, 10) || 0)
    };
  } catch {
    const dayKeys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return {
      dayKey: dayKeys[date.getDay()],
      minutes: date.getHours() * 60 + date.getMinutes()
    };
  }
}

export function businessPeriodMinutes(value = "") {
  const [hours, minutes] = String(value || "").split(":").map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
}

export function businessHoursToday(businessHours = null) {
  if (!businessHours?.weeklyHours) {
    return null;
  }
  const timezone = businessHours.timezone || "America/Los_Angeles";
  const local = businessHoursLocalParts(new Date(), timezone);
  const exception = (businessHours.exceptions || []).find((item) => {
    if (!item?.date) {
      return false;
    }
    try {
      const dateKey = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(new Date());
      return item.date === dateKey;
    } catch {
      return false;
    }
  });
  const schedule = exception || businessHours.weeklyHours[local.dayKey];
  if (!schedule || schedule.closed) {
    return {
      closed: true,
      label: exception?.reason ? `Closed today / ${exception.reason}` : "Closed today",
      status: "Closed today"
    };
  }
  const periods = (schedule.periods || []).filter((period) => period?.opens && period?.closes);
  if (periods.length === 0) {
    return {
      closed: true,
      label: "Hours not published today",
      status: "Hours not published"
    };
  }
  const label = periods.map((period) => `${period.opens} - ${period.closes}`).join(", ");
  const activePeriod = periods.find((period) => {
    const opens = businessPeriodMinutes(period.opens);
    const closes = businessPeriodMinutes(period.closes);
    return opens !== null && closes !== null && local.minutes >= opens && local.minutes < closes;
  });
  const nextPeriod = periods.find((period) => {
    const opens = businessPeriodMinutes(period.opens);
    return opens !== null && local.minutes < opens;
  });
  return {
    closed: false,
    label: `Today ${label}${timezone ? ` / ${timezone}` : ""}`,
    status: activePeriod ? "Open now" : nextPeriod ? `Opens today at ${nextPeriod.opens}` : "Closed for the day"
  };
}

export function resourceOpeningStatusLabel(resource = {}) {
  const availability = resource.memberAvailability || {};
  if (availability.label) {
    return availability.label;
  }
  const status = resource.availabilityPolicy?.publicationStatus || "open";
  const labels = {
    open: "Open for member reservations",
    members_only: "Members only",
    limited_preview: "Limited preview",
    under_construction: "Under construction",
    seasonal_closed: "Seasonally closed"
  };
  return labels[status] || "Open for member reservations";
}

export function resourceOpeningTimeLabel(resource = {}, businessHours = null) {
  const policy = resource.availabilityPolicy || {};
  const broadInternalWindow = policy.openTime === "00:00" && policy.closeTime === "23:59";
  const shouldShowResourceHours = policy.inheritRangeHours === false &&
    (resourceExplicitlyAllowsAfterHours(resource) || !broadInternalWindow);
  const shouldShowRangeHours = policy.inheritRangeHours !== false ||
    (!resourceExplicitlyAllowsAfterHours(resource) && broadInternalWindow);
  if (policy.openTime && policy.closeTime && shouldShowResourceHours) {
    return `${policy.openTime} - ${policy.closeTime}`;
  }
  const today = businessHoursToday(businessHours);
  if (shouldShowRangeHours && today?.label) {
    return today.label;
  }
  if (shouldShowRangeHours && businessHours?.summary) {
    return businessHours.summary;
  }
  if (shouldShowRangeHours && businessHours?.timezone) {
    return `Range hours / ${businessHours.timezone}`;
  }
  if (policy.startsOn || policy.endsOn) {
    return [policy.startsOn ? `Starts ${formatDateOnly(policy.startsOn)}` : "", policy.endsOn ? `Ends ${formatDateOnly(policy.endsOn)}` : ""].filter(Boolean).join(" / ");
  }
  return "Range hours";
}

export function memberResourceDisplayName(resource = {}) {
  const name = String(resource.name || resource.label || "").trim();
  if (/^Demo member bay \d{8,}$/i.test(name)) {
    return resource.laneNumber ? `Practice lane ${resource.laneNumber}` : "Member practice bay";
  }
  if (/^Phased member resource \d{8,}$/i.test(name)) {
    return "Member practice area";
  }
  return name || "Range area";
}

export function sortMemberResources(resources = []) {
  return [...(resources || [])].sort((left, right) => {
    const leftName = memberResourceDisplayName(left);
    const rightName = memberResourceDisplayName(right);
    const nameOrder = MEMBER_RESOURCE_NAME_COLLATOR.compare(leftName, rightName);
    if (nameOrder !== 0) {
      return nameOrder;
    }
    return MEMBER_RESOURCE_NAME_COLLATOR.compare(String(left.resourceId || ""), String(right.resourceId || ""));
  });
}

export function memberResourceKindLabel(resource = {}) {
  const labels = {
    bay: "Bay",
    lane: "Lane",
    classroom: "Classroom",
    area: "Area",
    target_pit: "Target pit"
  };
  const type = labels[resource.type] || "Area";
  const capacity = resource.capacity?.shooters ? `${resource.capacity.shooters} shooter${resource.capacity.shooters === 1 ? "" : "s"}` : "";
  const distance = resource.distance?.max ? `${resource.distance.max} yd` : "";
  return [type, capacity, distance].filter(Boolean).join(" / ");
}

export function memberResourceUnavailableReason(resource = {}) {
  const reason = String(resource.memberAvailability?.reason || "").trim();
  if (!reason) {
    return "Not open for self-service booking";
  }
  return reason
    .replace(/\bowner-published booking windows\b/gi, "booking windows")
    .replace(/\bresources?\b/gi, "areas")
    .replace(/\bself-service reservations?\b/gi, "online booking");
}

export function memberReservationResourceName(reservation = {}) {
  return memberResourceDisplayName({
    name: reservation.resource?.name || reservation.resourceSnapshot?.name || reservation.resourceName || ""
  });
}

export function memberEventDisplayTitle(event = {}) {
  const title = String(event.title || "").trim();
  if (/^Demo member range night \d{8,}$/i.test(title)) {
    return "Member range night";
  }
  return title || "Member event";
}

export function memberEventDisplayDescription(event = {}, fallback = "Event details will be confirmed by the range team.") {
  const description = String(event.description || event.summary || event.details || "").trim();
  if (/verify the member events workspace/i.test(description)) {
    return "Evening range session for members to check in, practice, and review arrival details before attending.";
  }
  return description || fallback;
}

export function rangeOpeningStatusLabel(businessHours = null, fallback = "Check hours before arrival") {
  return businessHoursToday(businessHours)?.status || fallback;
}

export function reservationIsToday(reservation = {}) {
  const startsAt = Date.parse(reservation.startsAt || "");
  if (!Number.isFinite(startsAt)) {
    return false;
  }
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return startsAt >= start.getTime() && startsAt < end.getTime();
}

export function reservationLimitUsage(activeReservations = [], eligibility = {}) {
  const maxActive = Number.parseInt(eligibility?.laneReservation?.maxActiveReservations || 0, 10) || 0;
  if (!maxActive) {
    return {
      label: "Limit set by the range",
      detail: "Booking rules apply when you choose a time.",
      reached: false,
      remaining: null,
      used: activeReservations.length
    };
  }
  const used = activeReservations.length;
  const remaining = Math.max(0, maxActive - used);
  return {
    label: `${used} of ${maxActive} active bookings used`,
    detail: remaining > 0 ? `${remaining} booking${remaining === 1 ? "" : "s"} remaining` : "Booking limit reached",
    reached: remaining <= 0,
    remaining,
    used
  };
}

export function categorizeMemberResources(resources = [], readinessBlocked = false, allowed = false, businessHours = null) {
  const categories = {
    reservable: [],
    restricted: [],
    unavailable: []
  };
  const parentIdsWithBookableChildren = new Set(
    resources
      .filter((resource) => (
        resource?.parentResourceId &&
        resource?.reservationRules?.enabled &&
        resource?.reservable !== false &&
        resource?.memberAvailability?.canReserve !== false
      ))
      .map((resource) => resource.parentResourceId)
  );
  resources.forEach((resource) => {
    if (parentIdsWithBookableChildren.has(resource?.resourceId)) {
      return;
    }
    const hasWindow = resourceHasReservableWindow(resource, new Date(), businessHours);
    const canReserve = Boolean(
      !readinessBlocked &&
      allowed &&
      resource?.reservationRules?.enabled &&
      resource?.reservable !== false &&
      resource?.memberAvailability?.canReserve !== false &&
      hasWindow
    );
    if (canReserve) {
      categories.reservable.push(resource);
    } else if (resource?.memberAvailability?.canReserve === false || !resource?.reservationRules?.enabled || resource?.reservable === false) {
      categories.restricted.push(resource);
    } else {
      categories.unavailable.push(resource);
    }
  });
  categories.reservable = sortMemberResources(categories.reservable);
  categories.restricted = sortMemberResources(categories.restricted);
  categories.unavailable = sortMemberResources(categories.unavailable);
  return categories;
}

export function reservationAdvanceWindowLabel(eligibility = {}) {
  const days = Number.parseInt(eligibility?.laneReservation?.advanceBookingDays || 0, 10) || 0;
  if (!days) {
    return "Booking window set by the range";
  }
  const latest = new Date();
  latest.setDate(latest.getDate() + days);
  return `Book through ${formatDateOnly(latest.toISOString())}`;
}

export function activeMaintenanceWindows(windows = []) {
  const now = Date.now();
  return (windows || [])
    .filter((window) => Date.parse(window.endsAt || "") >= now)
    .sort((left, right) => String(left.startsAt || "").localeCompare(String(right.startsAt || "")));
}

export function maintenanceWindowTitle(window = {}) {
  const resourceName = memberResourceDisplayName({ name: window.resource?.name || "" });
  const title = String(window.title || window.reason || "Maintenance").trim();
  return resourceName && resourceName !== "Range area" ? `${resourceName}: ${title}` : title;
}

export function formatReservationAccessLabel(member = {}) {
  const laneReservation = member.membershipPlan?.accessRules?.laneReservation || {};
  if (member.status !== "active") {
    return "Available after activation";
  }
  if (laneReservation.allowed) {
    return "Included for this plan";
  }
  return "Not included in this plan";
}
